import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import ApiService from 'services/apiService';
import { EndReasonTypeStatsData, EndReasonTypeFilter, EndReasonTypeTotals } from '../types';

interface EndReasonTypeDataContextType {
  data: EndReasonTypeStatsData[];
  loading: boolean;
  error: string | null;
  filter: EndReasonTypeFilter;
  setFilter: (filter: EndReasonTypeFilter) => void;
  totalsByYear: EndReasonTypeTotals[];
  grandTotal: {
    totalStudents: number;
    totalEnrolled: number;
    totalPercentage: number;
  } | null;
  activeSchoolYear: number | null;
}

const EndReasonTypeDataContext = createContext<EndReasonTypeDataContextType | undefined>(undefined);

export const EndReasonTypeDataProvider = ({ children }: { children: ReactNode }) => {
  const [data, setData] = useState<EndReasonTypeStatsData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [filter, setFilter] = useState<EndReasonTypeFilter>({
    schoolYears: [],
    selectedEndReasonTypes: [],
  });
  const [totalsByYear, setTotalsByYear] = useState<EndReasonTypeTotals[]>([]);
  const [grandTotal, setGrandTotal] = useState<{
    totalStudents: number;
    totalEnrolled: number;
    totalPercentage: number;
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await ApiService.instance.post(
          '/StudentEnrollments/GetEndReasonTypeStats',
          { 
            schoolYears: filter.schoolYears,
            selectedEndReasonTypeIds: filter.selectedEndReasonTypes || []
          }
        );

        const responseData = typeof response.data === 'string' 
          ? JSON.parse(response.data) 
          : response.data;

        if (responseData.activeSchoolYear && !activeSchoolYear) {
          setActiveSchoolYear(responseData.activeSchoolYear);
          
          if (!initialized && filter.schoolYears.length === 0) {
            setFilter({
              schoolYears: [responseData.activeSchoolYear],
              selectedEndReasonTypes: [],
            });
            setInitialized(true);
            setLoading(false);
            return;
          }
        }

        setData(responseData.data || []);
        setTotalsByYear(responseData.totalsByYear || []);
        setGrandTotal(responseData.grandTotal || null);
        setInitialized(true);
      } catch (err) {
        console.error('Error fetching end reason type data:', err);
        setError('Failed to load end reason type data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filter.schoolYears, filter.selectedEndReasonTypes]);

  return (
    <EndReasonTypeDataContext.Provider
      value={{ 
        data,
        loading, 
        error, 
        filter, 
        setFilter, 
        totalsByYear,
        grandTotal,
        activeSchoolYear 
      }}
    >
      {children}
    </EndReasonTypeDataContext.Provider>
  );
};

export const useEndReasonTypeData = () => {
  const context = useContext(EndReasonTypeDataContext);
  if (!context) {
    throw new Error('useEndReasonTypeData must be used within EndReasonTypeDataProvider');
  }
  return context;
};